import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {FormattedMessage, useIntl} from 'react-intl';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CircularProgress from '@mui/material/CircularProgress';
import {OtpVerifySmsCode} from 'wobi-web-common';
import {agentDeveloperUtils} from 'wobi-web-common/dist/components/utils/helpers';
import {Black} from 'wobi-web-common/dist/Colors';

const PHASE = {
  METHOD_SELECT: 'METHOD_SELECT',
  CODE_ENTRY: 'CODE_ENTRY',
};

const RATE_LIMIT_HINTS = ['rate', 'limit', 'blocked', 'Too many requests', '429'];

const matchesAny = (message, hints) => hints.some((hint) => message.includes(hint));

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    padding: '40px 20px',
    textAlign: 'center',
    direction: 'ltr',
  },
  title: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 12,
  },
  subtitle: {
    fontSize: theme.typography.pxToRem(16),
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.secondary,
    marginBottom: 16,
    lineHeight: 1.6,
  },
  maskedPhone: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    fontFamily: 'Ploni, Arial, sans-serif',
    color: theme.palette.text.primary,
    marginBottom: 32,
    direction: 'rtl',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    maxWidth: 340,
  },
  authButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '16px 24px',
    borderRadius: 30,
    border: 'none',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    fontFamily: 'Ploni, Arial, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  smsButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  whatsappButton: {
    backgroundColor: '#25D366',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1da851',
    },
  },
  disabledButton: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorMessage: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(14),
    fontFamily: 'Ploni, Arial, sans-serif',
    marginTop: 12,
  },
  codeEntryContainer: {
    width: '100%',
    maxWidth: 600,
    margin: '0 auto',
    '& .MuiGrid-container': {
      justifyContent: 'center',
    },
    '& .MuiGrid-container > .MuiGrid-root': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    '& strong': {
      direction: 'ltr',
      display: 'inline-block',
    },
  },
  // OtpVerifySmsCode required classes
  messageTop: {
    color: theme.palette.secondary.dark,
    direction: 'rtl',
    fontSize: theme.typography.pxToRem(20),
    textAlign: 'center',
    [theme.breakpoints.down('lg')]: {
      fontSize: theme.typography.pxToRem(18),
    },
  },
  formControl: {
    '& .MuiOutlinedInput-input': {
      fontWeight: 'bold',
    },
    width: '100%',
  },
  submitButton: {
    color: '#FAFAFA',
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bold',
    width: '100%',
  },
  textButton: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  linkAlt: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
    color: theme.palette.secondary.light,
    fontSize: theme.typography.pxToRem(16),
  },
  messageError: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  messageMutted: {
    color: Black[170],
    fontSize: theme.typography.pxToRem(18),
  },
  textVeryLarge: {
    color: agentDeveloperUtils.getPrimaryColor(450),
    fontSize: theme.typography.pxToRem(100),
    fontWeight: 300,
    letterSpacing: 0,
    lineHeight: '100px',
    [theme.breakpoints.down('lg')]: {
      fontSize: theme.typography.pxToRem(86),
      lineHeight: '86px',
    },
  },
  messageDefault: {
    color: theme.palette.secondary.dark,
  },
  withBorder: {
    borderColor: agentDeveloperUtils.getSecondaryColor(100),
    [theme.breakpoints.down('lg')]: {
      border: 'none',
    },
  },
}));

/**
 * OTP authentication screen shared by every short-link flow.
 * Callers own the transport (send/verify) and what happens once authenticated.
 */
const OtpAuthScreen = ({
  authMethods,
  maskedPhone,
  titleId,
  titleValues,
  descriptionId,
  descriptionMaxWidth,
  onSendCode,
  onVerifyCode,
  onAuthenticated,
}) => {
  const {classes} = useStyles();
  const intl = useIntl();

  const [phase, setPhase] = useState(PHASE.METHOD_SELECT);
  const [sendLoading, setSendLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentMethod, setSentMethod] = useState('');

  const resolveSendError = useCallback((err) => {
    const message = err?.message || '';
    const id = matchesAny(message, RATE_LIMIT_HINTS) ? 'otpAuth.rateLimitError' : 'otpAuth.sendError';

    return intl.formatMessage({id});
  }, [intl]);

  const resolveVerifyError = useCallback((err) => {
    const message = err?.message || '';
    let id = 'otpAuth.verifyError';
    if (message.includes('maxAttempts')) {
      id = 'otpAuth.maxAttempts';
    } else if (matchesAny(message, ['wrongOtp', 'invalid'])) {
      id = 'otpAuth.wrongCode';
    } else if (matchesAny(message, RATE_LIMIT_HINTS)) {
      id = 'otpAuth.rateLimitError';
    }

    return intl.formatMessage({id});
  }, [intl]);

  const handleSendCode = useCallback(async (method) => {
    setError('');
    setSentMethod(method);
    setSendLoading(true);
    try {
      await onSendCode(method);
      setPhase(PHASE.CODE_ENTRY);
    } catch (err) {
      setError(resolveSendError(err));
    } finally {
      setSendLoading(false);
    }
  }, [onSendCode, resolveSendError]);

  const handleVerify = useCallback(async (data) => {
    setError('');
    setVerifyLoading(true);
    try {
      const result = await onVerifyCode(data.code);
      if (result?.authStatus !== 'AUTHENTICATED') {
        setError(intl.formatMessage({id: 'otpAuth.verifyError'}));
        return;
      }
      await onAuthenticated();
    } catch (err) {
      setError(resolveVerifyError(err));
    } finally {
      setVerifyLoading(false);
    }
  }, [onVerifyCode, onAuthenticated, resolveVerifyError, intl]);

  const handleResend = useCallback(async (otpMethod) => {
    setError('');
    try {
      await onSendCode(otpMethod);
    } catch (err) {
      setError(resolveSendError(err));
    }
  }, [onSendCode, resolveSendError]);

  const handleBack = useCallback(() => {
    setError('');
    setPhase(PHASE.METHOD_SELECT);
  }, []);

  const normalizedMethods = (authMethods || []).map((method) => method.toUpperCase());

  if (phase === PHASE.CODE_ENTRY) {
    return (
      <div className={classes.container}>
        <div className={classes.codeEntryContainer}>
          <OtpVerifySmsCode
            classes={classes}
            data={{phoneNumber: maskedPhone}}
            commonErrorMessage={error}
            clearError={() => setError('')}
            handleVerify={handleVerify}
            handleBack={handleBack}
            handleResend={handleResend}
            isLoading={verifyLoading}
            otpMethods={normalizedMethods}
            submitButtonText={intl.formatMessage({id: 'otpAuth.verifyButton'})}
            hideBackButton
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <FormattedMessage id={titleId} values={titleValues} />
      </div>
      <div
        className={classes.subtitle}
        style={descriptionMaxWidth ? {maxWidth: descriptionMaxWidth} : undefined}
      >
        <FormattedMessage id={descriptionId} />
      </div>
      {maskedPhone && (
        <div className={classes.maskedPhone}>{maskedPhone}</div>
      )}
      <div className={classes.buttonsContainer}>
        {normalizedMethods.includes('SMS') && (
          <button
            type='button'
            className={`${classes.authButton} ${classes.smsButton} ${sendLoading ? classes.disabledButton : ''}`}
            onClick={() => handleSendCode('SMS')}
            disabled={sendLoading}
          >
            {sendLoading && sentMethod === 'SMS' ? <CircularProgress size={20} color='inherit' /> : <SmsOutlinedIcon />}
            <FormattedMessage id='otpAuth.sms' />
          </button>
        )}
        {normalizedMethods.includes('WHATSAPP') && (
          <button
            type='button'
            className={`${classes.authButton} ${classes.whatsappButton} ${sendLoading ? classes.disabledButton : ''}`}
            onClick={() => handleSendCode('WHATSAPP')}
            disabled={sendLoading}
          >
            {sendLoading && sentMethod === 'WHATSAPP' ? <CircularProgress size={20} color='inherit' /> : <WhatsAppIcon />}
            <FormattedMessage id='otpAuth.whatsapp' />
          </button>
        )}
      </div>
      {error && <div className={classes.errorMessage}>{error}</div>}
    </div>
  );
};

OtpAuthScreen.propTypes = {
  authMethods: PropTypes.arrayOf(PropTypes.string),
  descriptionId: PropTypes.string.isRequired,
  descriptionMaxWidth: PropTypes.number,
  maskedPhone: PropTypes.string,
  onAuthenticated: PropTypes.func.isRequired,
  onSendCode: PropTypes.func.isRequired,
  onVerifyCode: PropTypes.func.isRequired,
  titleId: PropTypes.string.isRequired,
  titleValues: PropTypes.object,
};

export default OtpAuthScreen;
